import { Cell, Grid, useTheme } from 'bold-ui'
import { dateFnsDefaultLocale } from 'components/agenda/agendaLocalizer'
import { SelectField } from 'components/form'
import { CustomSingleSelectFieldProps, ErrorField } from 'components/form/final-form'
import { addMinutes, format } from 'date-fns'
import { useHorarioAgendaSelectFieldQuery } from 'graphql/hooks.generated'
import { ReactComponent as AgendaOnlineIcon } from 'images/agenda/agenda-online.svg'
import { useCallback, useEffect, useMemo } from 'react'
import { useField } from 'react-final-form'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'

import { getItemsHorarioFinal } from './getItemsHorarioFinal'
import { getProximoHorarioIndisponivel } from './getProximoHorarioIndisponivel'
import { hasHorarioOcupadoNoPeriodo } from './hasHorarioOcupadoNoPeriodo'

export interface HorarioAgendaSelectFieldModel {
  inicial: Date
  final: Date
}

export type HorarioAgendaSelectFieldProps = DistributiveOmit<
  CustomSingleSelectFieldProps<Date>,
  'name' | 'label' | 'value'
> & {
  name: MetaPath<HorarioAgendaSelectFieldModel>
  lotacaoId: ID
  outraLotacaoIdVerificarDisponibilidade?: ID
  showHorarioFinal?: boolean
  dia: Date | undefined
  value?: HorarioAgendaSelectFieldModel
  labels?: { inicial: string; final?: string }
  verificarReservasAgendaOnline?: boolean
  atencaoDomiciliar?: boolean
  agendamentosIdsDesconsiderar?: Array<ID>
}

const itemToString = (item: Date) => item && format(item, 'HH:mm', { locale: dateFnsDefaultLocale })

export function HorarioAgendaSelectField(props: Readonly<HorarioAgendaSelectFieldProps>) {
  const {
    name,
    lotacaoId,
    outraLotacaoIdVerificarDisponibilidade,
    value,
    dia,
    showHorarioFinal = false,
    labels = { inicial: 'Horário inicial', final: 'Horário final' },
    disabled,
    verificarReservasAgendaOnline = true,
    atencaoDomiciliar = false,
    agendamentosIdsDesconsiderar,
    ...rest
  } = props

  const theme = useTheme()

  const isDiaVazio = !dia

  const { data: { horariosAgenda } = {}, loading } = useHorarioAgendaSelectFieldQuery({
    variables: {
      input: {
        lotacaoId,
        outraLotacaoIdVerificarDisponibilidade,
        dia: dateAsYyyyMmDd(dia),
        isAtencaoDomiciliar: atencaoDomiciliar,
        agendamentosIdsDesconsiderar: agendamentosIdsDesconsiderar?.filterNotNull(),
      },
    },
    skip: isDiaVazio || disabled || !lotacaoId,
  })

  const {
    input: { value: horarioInicial, onChange: onChangeHorarioInicial, onBlur: onBlurHorarioInicial },
  } = useField<Date>(name.inicial.absolutePath())
  const {
    input: { onChange: onChangeHorarioFinal },
  } = useField<Date>(name.final.absolutePath())

  // TODO https://github.com/laboratoriobridge/pec/issues/29585
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setHorarioInicial = useCallback(onChangeHorarioInicial, [name.inicial])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setHorarioFinal = useCallback(onChangeHorarioFinal, [name.final])

  const horariosMap = useMemo(() => new Map(horariosAgenda?.map((h) => [new Date(h.horario), h])), [horariosAgenda])

  useEffect(() => {
    if (!loading) {
      const horarioDia = horarioInicial
        ? Array.from(horariosMap?.keys() ?? []).find(
            (h) => h.getHours() === horarioInicial.getHours() && h.getMinutes() === horarioInicial.getMinutes()
          )
        : undefined

      setHorarioInicial(horarioDia)
      onBlurHorarioInicial() //Necessário para revalidar o campo
    }
  }, [horariosMap, horarioInicial, setHorarioInicial, loading, onBlurHorarioInicial])

  useEffect(() => {
    if (isDiaVazio) {
      setHorarioFinal(undefined)
    }
  }, [isDiaVazio, setHorarioFinal])

  // TODO (RNG: #31450): Quando todos os módulos possuírem horário final, remover esse useEffect
  useEffect(() => {
    if (!horarioInicial) {
      setHorarioFinal(undefined)
    } else if (!loading && !showHorarioFinal) {
      const horario = horariosMap.get(horarioInicial)
      if (horario) {
        setHorarioFinal(addMinutes(horarioInicial, horario.duracao))
      }
    }
  }, [horarioInicial, horariosMap, loading, setHorarioFinal, showHorarioFinal])

  const isReservadoAgendaOnline = useCallback(
    (date: Date) => verificarReservasAgendaOnline && !!horariosMap.get(date)?.isReservadoAgendaOnline,
    [horariosMap, verificarReservasAgendaOnline]
  )

  const proximoHorarioOcupado = useMemo(
    () =>
      getProximoHorarioIndisponivel(
        horarioInicial,
        horariosAgenda?.map((h) => ({
          ...h,
          isOcupado: h.isOcupado || (verificarReservasAgendaOnline && h.isReservadoAgendaOnline),
        }))
      ),
    [horarioInicial, horariosAgenda, verificarReservasAgendaOnline]
  )

  const horariosFinais = useMemo(
    () => showHorarioFinal && getItemsHorarioFinal(horariosMap, horarioInicial, proximoHorarioOcupado),
    [horarioInicial, horariosMap, proximoHorarioOcupado, showHorarioFinal]
  )

  return (
    <Grid>
      <Cell size={showHorarioFinal ? 6 : 12}>
        <SelectField<Date>
          label={labels.inicial}
          value={value?.inicial}
          items={Array.from(horariosMap.keys()).sort()}
          itemToString={itemToString}
          itemIsEqual={(h) => horariosMap.get(h)?.isOcupado || isReservadoAgendaOnline(h)}
          customText={(h) => (horariosMap.get(h)?.isOcupado ? 'Ocupado' : 'Reservado')}
          selectedItemStyle={(h) =>
            horariosMap.get(h)?.isOcupado
              ? { icon: 'banOutline', color: theme.pallete.text.disabled }
              : { icon: AgendaOnlineIcon, color: theme.pallete.status.info.main }
          }
          selectedItemTooltip={(h) =>
            !horariosMap.get(h)?.isOcupado && isReservadoAgendaOnline(h) && 'Reservado para agenda online'
          }
          name={name.inicial}
          disabled={disabled}
          validate={(h) =>
            horariosMap.get(h)?.isOcupado || isReservadoAgendaOnline(h) ? 'Horário ocupado' : undefined
          }
          {...rest}
        />
        <ErrorField name={name} ignoreObjectError />
      </Cell>
      {showHorarioFinal && (
        <Cell size={6}>
          <SelectField<Date>
            label={labels.final}
            value={value?.final}
            items={horariosFinais}
            itemToString={itemToString}
            name={name.final}
            disabled={disabled || !horarioInicial}
            validate={(horarioFinal) =>
              hasHorarioOcupadoNoPeriodo(horarioInicial, horarioFinal, horariosAgenda, verificarReservasAgendaOnline)
                ? 'Horário possui agendamento / reserva'
                : undefined
            }
            {...rest}
          />
        </Cell>
      )}
    </Grid>
  )
}
